import React from "react";
import { Modal, Spinner } from "ui/components";
import { Fragment } from "core/models";
import styles from "./InfoModal.module.scss";

interface Props {
    fragment: Fragment;
    handleClose(): void;
}

const InfoModal: React.FC<Props> = ({ fragment, handleClose }) => {
    const dimensions = fragment.getMediaDimensions();

    type FragmentProps = {
        iscompilation?: boolean;
        fromfile?: boolean;
        cropped?: boolean;
        introkey?: string;
        isduplicate?: boolean;
        outrokey?: string;
        overlaykey?: string;
        padded?: boolean;
        trimmed?: boolean;
        trimstart?: number;
        trimend?: number;
    };
    const [fragmentProps, setFragementProps] = React.useState<FragmentProps>(
        {},
    );

    const getProps = () => {
        const newFragmentProps: FragmentProps = {};
        fragment.properties.forEach((prop) => {
            if (typeof prop.value !== "object") {
                return;
            }
            if ("iscompilation" in prop.value) {
                newFragmentProps.iscompilation = prop.value.iscompilation;
            }
            if ("fromfile" in prop.value) {
                newFragmentProps.fromfile = prop.value.fromfile;
            }
            if ("cropped" in prop.value) {
                newFragmentProps.cropped = prop.value.cropped;
            }
            if ("introkey" in prop.value) {
                newFragmentProps.introkey = prop.value.introkey;
            }
            if ("isduplicate" in prop.value) {
                newFragmentProps.isduplicate = prop.value.isduplicate;
            }
            if ("outrokey" in prop.value) {
                newFragmentProps.outrokey = prop.value.outrokey;
            }
            if ("overlaykey" in prop.value) {
                newFragmentProps.overlaykey = prop.value.overlaykey;
            }
            if ("padded" in prop.value) {
                newFragmentProps.padded = prop.value.padded;
            }
            if ("trimmed" in prop.value) {
                newFragmentProps.trimmed = prop.value.trimmed;
            }
            if ("trimstart" in prop.value) {
                newFragmentProps.trimstart = prop.value.trimstart;
            }
            if ("trimend" in prop.value) {
                newFragmentProps.trimend = prop.value.trimend;
            }
        });
        setFragementProps(newFragmentProps);
    };

    React.useEffect(() => {
        getProps();
    }, []);

    const infoList = [
        {
            "clip ID": fragment.id,
            "Clip source ID": fragment.groupID, // Should be source fragment ID (get from group)
            "Create date": new Date(
                `${fragment.creationDate}Z`,
            ).toLocaleString(),
            "Stream date": new Date(
                `${fragment.broadcastDate}Z`,
            ).toLocaleString(),
            "Stream name": fragment.channelProperties
                ? fragment.channelProperties.name
                : "",
        },
        {
            Duration: `${fragment.getDuration()}s`,
            Resolution: `${dimensions.width}x${dimensions.height} (${fragment.getAspectRatio()})`,
            Framerate: `${fragment.getFrameRateDouble()}fps`,
            "Video bitrate": fragment.getVideoBitrate(),
            "Audio bitrate": fragment.getAudioBitrate(),
        },
        {
            Uploaded: fragmentProps.fromfile ? "Yes" : "No",
            Compilation: fragmentProps.iscompilation ? "Yes" : "No",
            "Is a duplicate": fragmentProps.isduplicate ? "Yes" : "No",
            Trimmed: fragmentProps.trimend
                ? `${fragmentProps.trimstart}s - ${fragmentProps.trimend}s`
                : "No",
            Resized: fragmentProps.padded ? "Yes" : "No",
            Cropped: fragmentProps.cropped ? "Yes" : "No",
        },
        {
            Overlay: fragmentProps.overlaykey || "None",
            Intro: fragmentProps.introkey || "None",
            Outro: fragmentProps.outrokey || "None",
        },
    ];

    const modalContent = (
        <div className={styles.list}>
            {infoList.map((group, groupIndex) => (
                <div className={styles.list__group} key={groupIndex}>
                    {Object.entries(group).map(([name, value]) => (
                        <div className={styles.list__item} key={name + value}>
                            <span>{name}</span>
                            <span>{value ?? <Spinner size="small" />}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            theme="dark"
            onClose={handleClose}
            saveButtonDisabled
            cancelButtonText="Back"
            title={fragment.title}
            icon="info"
            takeContentSize
            saveButtonVisible={false}
        />
    );
};

export default InfoModal;
